import {useRef, useState} from 'react';

import type {PublicOption, VotingSystem} from '../../../shared/ballotTypes';
import {FaceCluster} from '../../../shared/components/faceCluster';
import {CheckIcon, MinusIcon, PlusIcon} from '../../../shared/components/icons';
import {SlackText} from '../../../shared/components/slackText';
import {StickerCard} from '../../../shared/sticker/stickers';

const HOLD_MS = 450;

// Fixed-width slot so titles line up whether a card shows one face or a cluster.
const FACE_SLOT = 76;

const Face = ({option}: {option: PublicOption}) => (
    <span className="ph-option__face">
        <FaceCluster
            title={option.title}
            photos={option.photo_urls}
            size={40}
            maxWidth={FACE_SLOT}
        />
    </span>
);

export const OptionCard = ({
    option,
    system,
    value,
    onTap,
    onStep,
}: {
    option: PublicOption;
    system: VotingSystem;
    value: number | undefined;
    onTap: () => void;
    onStep: (delta: 1 | -1) => void;
}) => {
    const [expanded, setExpanded] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const held = useRef(false);
    const selected = value !== undefined;

    const startHold = () => {
        held.current = false;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            held.current = true;
            setExpanded((open) => !open);
        }, HOLD_MS);
    };
    const endHold = () => clearTimeout(timer.current);

    const text = (
        <span className="ph-option__text">
            <b>{option.title}</b>
            {option.description && (
                <small
                    className={
                        expanded
                            ? 'ph-option__reason'
                            : 'ph-option__reason ph-option__reason--clamped'
                    }
                >
                    <SlackText text={option.description} />
                </small>
            )}
        </span>
    );
    const holdProps = {
        onPointerDown: startHold,
        onPointerUp: endHold,
        onPointerLeave: endHold,
        onPointerCancel: endHold,
        onContextMenu: (e: React.SyntheticEvent) => e.preventDefault(),
    };

    return (
        <StickerCard
            seed={option.id}
            className={selected ? 'ph-option ph-option--sel' : 'ph-option'}
        >
            {system === 'dots' ? (
                <div className="ph-option__body" {...holdProps}>
                    <Face option={option} />
                    {text}
                    <span className="ph-stepper">
                        <button
                            type="button"
                            aria-label={`Remove a dot from ${option.title}`}
                            disabled={!selected}
                            onClick={() => onStep(-1)}
                        >
                            <MinusIcon />
                        </button>
                        <output>{value ?? 0}</output>
                        <button
                            type="button"
                            aria-label={`Add a dot to ${option.title}`}
                            onClick={() => onStep(1)}
                        >
                            <PlusIcon />
                        </button>
                    </span>
                </div>
            ) : (
                <button
                    type="button"
                    className="ph-option__body"
                    aria-pressed={selected}
                    onClick={() => {
                        if (held.current) {
                            held.current = false;
                            return;
                        }
                        onTap();
                    }}
                    {...holdProps}
                >
                    <Face option={option} />
                    {text}
                    <span className="ph-badge" aria-hidden="true">
                        {selected && (system === 'preferential' ? value : <CheckIcon />)}
                    </span>
                </button>
            )}
        </StickerCard>
    );
};
